import React from 'react';
import { Box, Stack } from '@mui/material';
import { Skeleton } from '@repo/ui';
import { LIST_ROW_LEADING_SIZE } from 'components/UI';

interface ScansListSkeletonProps {
  /** Number of placeholder rows. Defaults to a screen's worth on mobile. */
  rows?: number;
}

/**
 * Loading placeholder for the Scans list (VERA-476).
 *
 * Mirrors the real row's footprint — the shared `ListRow` leading circle plus the
 * two stacked text lines and the right-aligned timestamp — so the swap to loaded
 * content causes no layout shift. Replaces the previous centred "Loading scans..."
 * text, per the skeleton standing rule (§19). Sizes come from
 * `LIST_ROW_LEADING_SIZE` so the skeleton can't drift from the row again.
 *
 * No status-chip placeholder: the chip only renders for exceptional scans
 * (invalid / suspicious link), so reserving space for it on every row would
 * itself be the layout shift.
 */
export const ScansListSkeleton: React.FC<ScansListSkeletonProps> = ({ rows = 6 }) => {
  return (
    <Stack spacing={0} aria-busy='true' aria-label='Loading scans'>
      {Array.from({ length: rows }).map((_, i) => (
        <Box
          key={i}
          sx={theme => ({
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            py: 2,
            borderBottom: `1px solid ${theme.palette.grey[200]}`,
          })}
        >
          <Skeleton
            variant='circular'
            width={LIST_ROW_LEADING_SIZE}
            height={LIST_ROW_LEADING_SIZE}
          />
          <Stack spacing={1} sx={{ flexGrow: 1, minWidth: 0 }}>
            <Skeleton width='40%' height={14} />
            <Skeleton width='55%' height={12} />
          </Stack>
          <Skeleton width={70} height={12} />
        </Box>
      ))}
    </Stack>
  );
};

export default ScansListSkeleton;
